import { type LogPanelState } from "@/shared/state/visualization";

// Fallback for a topic with an unknown message count, so it still contributes
// to the total instead of nothing.
const DEFAULT_ROWS_PER_TOPIC = 20;

// Upper bound on skeleton rows shown while the index loads. At a 35px row height
// this fills a tall viewport (~1750px); a heavy topic's real count would size the
// panel to millions of rows, which the loading state never needs to show.
export const MAX_SKELETON_ROWS = 50;

/**
 * Estimate the panel's total row count from its topics' message counts. Sums
 * each distinct topic once (a topic recurs once per column), falling back per
 * topic so a missing count doesn't under-count the total.
 *
 * Feeds the "large dataset" gate that decides whether building the search index
 * needs user confirmation, so it must reflect the real message count.
 */
export function estimateTopicMessageCount(data: LogPanelState["data"]): number {
  const counted = new Set<string>();
  let total = 0;
  for (const datum of data) {
    if (counted.has(datum.topic.id)) {
      continue;
    }
    counted.add(datum.topic.id);
    total += datum.topic.messageCount ?? DEFAULT_ROWS_PER_TOPIC;
  }
  return total > 0 ? total : DEFAULT_ROWS_PER_TOPIC;
}

/**
 * Rows to render while the index loads: the message-count estimate capped at
 * {@link MAX_SKELETON_ROWS}, so the loading panel fills the viewport without
 * pretending a heavy topic's worth of rows already exists.
 */
export function skeletonRowCount(topicMessageCount: number): number {
  return Math.min(topicMessageCount, MAX_SKELETON_ROWS);
}
